import { matchRoutes, RouteObject, useNavigate, useRoutes } from "react-router-dom";
import NotFound from "../components/Layouts/Errors/NotFound";
import { useAuth } from "react-oidc-context";
import { observer } from "mobx-react-lite";
import { useStore } from "../root-store-context";
import { useEffect, useState, useRef } from "react";
import Main from "../components/Layouts/Main/Main";
import Error from "../components/Layouts/Errors/Error";
import Loading from "../components/Layouts/Errors/Loading";
import UserNotFound from "../components/Layouts/Errors/UserNotFound";
import NoCommunities from "../components/Layouts/Errors/NoCommunities";
import { AxiosError } from "axios";
import { RouteElement, RouteGroupElement } from "../stores/appStores/ApplicationStore";
import { CommunityProxy, Permission } from "../api/Common/CommonTypes";
import { App } from "antd";
import { ErrorResponse, UserManager } from "oidc-client-ts";
import MaintenancePage from "../pages/Maintenance/MaintenancePage";
import { installSessionExpiredInterceptor } from "../api/base/sessionExpiredInterceptor";

const readSigninMaintenance = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    message: params.get("maintenance_message") ?? "",
    endsAt: params.get("maintenance_ends_at"),
  };
};

const Router: React.FC = observer(() => {
  const auth = useAuth();
  const rootStore = useStore();
  const { apiStore, applicationStore } = rootStore;
  const [routings, setRoutings] = useState<RouteObject[]>([]);
  const navigate = useNavigate();
  const { notification } = App.useApp();
  
  // Refs to prevent multiple simultaneous redirects (Firefox issue)
  const isRedirectingRef = useRef(false);
  const forceLoginRef = useRef(false);
  const hasCheckedAuthRef = useRef(false);
  // On load only: a stale stored token gets a fresh sign-in, never a sign-out.
  // getUser() hands back the stale user because localStorage outlives the IdP
  // session cookie, and signing out here dropped the resident on the IdP's
  // "Are you sure you want to sign out?"
  useEffect(() => {
    if (!auth.user || auth.isLoading || isRedirectingRef.current) {
      return;
    }

    const expiresAt = auth.user.expires_at;
    if (expiresAt === undefined || expiresAt > Date.now() / 1000) {
      return;
    }

    console.log('Token expired - signing in again');
    isRedirectingRef.current = true;
    hasCheckedAuthRef.current = true;

    applicationStore.clearCommunitiesLocalStorage();
    rootStore.clearLocalStorages();
    localStorage.removeItem("addressId");

    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    auth.signinRedirect({ state: { returnTo } }).catch((error) => {
      console.error('Sign in redirect error:', error);
      isRedirectingRef.current = false;
      hasCheckedAuthRef.current = false;
    });
  }, [auth.user?.expires_at, auth.isLoading]);

  // Handle authentication redirect - centralized and Firefox-safe
  useEffect(() => {
    // Skip if already checked, loading, authenticated, has error, or already redirecting
    if (hasCheckedAuthRef.current || auth.isLoading || auth.isAuthenticated || auth.error || isRedirectingRef.current) {
      return;
    }

    // Check if we're in OAuth callback flow
    const urlParams = new URLSearchParams(window.location.search);
    const isCallback = 
      window.location.pathname.includes('/callback') ||
      urlParams.has('code') ||
      urlParams.has('state') ||
      urlParams.has('session_state');

    if (isCallback) {
      console.log('OAuth callback detected - waiting for auth to complete');
      return;
    }

    // Mark as checked to prevent multiple checks
    hasCheckedAuthRef.current = true;

    // Not authenticated and not in callback - need to redirect
    console.log('User not authenticated - initiating sign in');
    isRedirectingRef.current = true;
    
    applicationStore.clearCommunitiesLocalStorage();
    rootStore.clearLocalStorages();
    // Drop the in-session community pick so a fresh login starts from the
    // token's community instead of inheriting a prior session's selection.
    localStorage.removeItem("addressId");

    // Remember the page the user was heading to, so we can return after sign-in.
    const returnTo = window.location.pathname + window.location.search + window.location.hash;

    // Small delay for Firefox to prevent race conditions
    const redirectTimer = setTimeout(() => {
      const prompt = forceLoginRef.current ? "login" : undefined;
      auth.signinRedirect({ prompt, state: { returnTo } }).catch((error) => {
        console.error('Sign in redirect error:', error);
        isRedirectingRef.current = false;
        hasCheckedAuthRef.current = false;
      });
    }, 150);

    return () => {
      clearTimeout(redirectTimer);
    };
  }, [auth.isAuthenticated, auth.isLoading, auth.error]);

  const isMaintenanceSigninError =
    auth.error instanceof ErrorResponse && auth.error.error === "temporarily_unavailable";

  useEffect(() => {
    if (isMaintenanceSigninError) {
      window.history.replaceState({}, document.title, "/");
    }
  }, [isMaintenanceSigninError]);

  useEffect(() => installSessionExpiredInterceptor(() => {
    forceLoginRef.current = true;
    return auth.removeUser();
  }), [auth.removeUser]);

  // Reset redirect flag when authentication succeeds
  useEffect(() => {
    if (auth.isAuthenticated) {
      isRedirectingRef.current = false;
      hasCheckedAuthRef.current = false;
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (
      routings.length ||
      applicationStore?.SelectedAddress?.id ||
      !applicationStore.CommunityList
    ) {
      applicationStore.editLoading(false);
    }
  }, [routings, applicationStore]);

  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinRedirect({
        state: { returnTo: window.location.pathname + window.location.search + window.location.hash },
      }).catch((error) => {
        console.error('Sign in redirect error:', error);
      });
    });
  }, [auth.events]);

  // useEffect(() => {
  //   if (auth.user) {
  //     const isExpired = auth.user.expires_at ? auth.user.expires_at > Date.now()/1000 : true; // Convert seconds to milliseconds
  //     console.log(isExpired);
  //     console.log(auth.user.expires_at);
  //     console.log(Date.now()/1000);
  //     if (isExpired) {
  //       // Token has expired
  //       console.log('Token expired');
  //       applicationStore.clearCommunitiesLocalStorage();
  //       rootStore.clearLocalStorages();
  //       auth.signinRedirect();
  //       // Handle expired token, e.g., redirect to login or refresh token
  //     } else {
  //       // Token is still valid
  //       console.log(auth.user);
  //       console.log('Token is valid');
  //     }
  //   } else {
  //    // User is not logged in
  //     console.log('User is not logged in');
  //   }
  // });
  
  const setStores = (data: CommunityProxy[]) => {
    const firstCommunity = data.length > 0 ? data[0] : undefined;
    if (!firstCommunity) {
      applicationStore.editLoading(false);
      return;
    }

    // 1. Honor an explicit in-session selection. addressId is written on every
    //    selection (including the combo switch that triggers a refresh reload)
    //    and cleared on the pre-login redirect, so its presence means "picked
    //    this session" and must win — otherwise the post-switch reload snaps
    //    back to the old community. Bad ids fall through (no-op if not in list).
    const localStorageAddressId = localStorage.getItem("addressId");
    if (localStorageAddressId != null) {
      applicationStore.setSelectedCommunityById(localStorageAddressId);
    }

    // 2. Fresh login → match the community the token is bound to. contact_key
    //    uniquely identifies one resident-contact (hence one community), and
    //    it's the only community-identifying claim that reaches the SPA:
    //    resident_key/company_key are access-token-only, contact_key is in the
    //    id token + userinfo. Keeps the combo in sync with what SSO hand-offs use.
    if (applicationStore.SelectedAddress.communityId === undefined) {
      const tokenContactKey = auth.user?.profile?.contact_key as string | undefined;
      const tokenMatch =
        tokenContactKey != null && tokenContactKey !== "" && tokenContactKey !== "0"
          ? data.find(
              (c) => String(c.residentContactKey ?? "") === String(tokenContactKey)
            )
          : undefined;
      if (tokenMatch) {
        applicationStore.setSelectedCommunity(tokenMatch);
      }
    }

    // 3. Still nothing matched → first available.
    if (applicationStore.SelectedAddress.communityId === undefined) {
      applicationStore.setSelectedCommunity(firstCommunity);
    }

    rootStore.refreshStores();
  };

  useEffect(() => {
    const newRoutings: RouteObject[] = [];
    applicationStore.routingList.forEach((item) => {
      const items: RouteObject[] = [];
      item.children?.forEach((subItem: RouteElement) => {
        if (applicationStore.isFeatures(subItem.featureID)) {
          // Handle accordion items with nested children
          if (subItem.isAccordion && subItem.children) {
            subItem.children.forEach((nestedItem: RouteElement) => {
              if (applicationStore.isFeatures(nestedItem.featureID) && nestedItem.element) {
                items.push({
                  path: `${item.path}${nestedItem.path}`,
                  element: nestedItem.element,
                });
              }
            });
          } else if (subItem.element) {
            items.push({
              path: `${item.path}${subItem.path}`,
              element: subItem.element,
            });
          }
        }
      });
      if (items.length) {
        newRoutings.push({
          path: `/`,
          element: items[0].element,
        });
      }
      newRoutings.push(...items);
    });

    const pathname = window.location.pathname;
    if (applicationStore.isLoading) {
      if (newRoutings.length && !matchRoutes(newRoutings, pathname)) {
        navigate("/", { replace: true });
      }
    } else if (!newRoutings.some((i) => i.path === pathname)) {
      navigate("/");
    }

    setRoutings(newRoutings);
  }, [applicationStore.routingList]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      apiStore.setConfigApi({
        authToken: auth.user.profile.userToken as string,
        userEmail: auth.user.profile.email as string,
        userId: auth.user.profile.userId as string,
        community: {} as CommunityProxy,
        notification: notification,
      });

      // One check at sign-in: the boot path below can serve communities from
      // localStorage and issue no request of its own.
      void applicationStore.refreshMaintenanceStatus();
    }
    if (auth.isAuthenticated && applicationStore.CommunityList.length === 0) {
      const localData = applicationStore.getCommunitiesFromLocalStorage();
      if (localData === null) {
        apiStore.CommonApiClient.getAllForBoardMember()
          .then((res) => {
            // console.log(res);
            applicationStore.setCommunityList(res.data);
            applicationStore.saveCommunitiesToLocalStorage(res.data);
            setStores(res.data);
          })
          .catch((err: AxiosError) => {
            console.log(err);
            applicationStore.editLoading(false);
          });
      } else {
        applicationStore.setCommunityList(localData);
        setStores(localData);
      }      
    }
  }, [auth.isAuthenticated]);

  const onLogOut = () => {
    const args = {
      extraQueryParams: {
        post_logout_redirect_uri: process.env.REACT_APP_REDIRECT_URI as string,
      },
    };
    void auth.signoutRedirect(args);
  };

  const maintenanceScreen = (
    <MaintenancePage
      reason={applicationStore.maintenanceReason}
      message={applicationStore.maintenanceMessage}
      endsAt={applicationStore.maintenanceEndsAt}
      onRetry={applicationStore.retryAfterOutage}
    />
  );

  let routes: RouteObject[] = [
    {
      path: "/",
      element: (
        <Main
          key={applicationStore.ApiStore.configApi.community?.communityId}
        />
      ),
      children: [
        ...routings,
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
    {
      path: "/maintenance",
      element: maintenanceScreen,
    },
  ];

  if (isMaintenanceSigninError) {
    routes = [{ path: "*", element: <MaintenancePage reason="maintenance" onRetry={() => window.location.assign("/")} /> }];
  } else if (auth.error) {
    routes = [{ path: "*", element: <Error /> }];
  } else {
    if (auth.isAuthenticated) {
      if (applicationStore.maintenanceActive) {
        routes = [{ path: "*", element: maintenanceScreen }];
      } else if (applicationStore.isLoading) {
        routes = [{ path: "*", element: <Loading /> }];
      } else if (auth.user?.profile.userId === undefined) {
        routes = [{ path: "*", element: <UserNotFound /> }];
      } else if (applicationStore.SelectedAddress.communityId === undefined) {
        routes = [{ path: "*", element: <NoCommunities /> }];
      }
    } else {
      routes = [{ path: "*", element: <Loading /> }];
    }
  }
  return useRoutes(routes);
});

export default Router;
